<template>
    <div id="app" :class="appClass">
        <b-loading :is-full-page="true" :active.sync="loading" :can-cancel="false">
            <b-icon
                    icon="sync-alt"
                    custom-class="fa-spin">
            </b-icon>
            <p>Initializing {{ title }} systems, please stand by.</p>
        </b-loading>

        <b-loading id="network-error-overlay" :is-full-page="true" :active.sync="showNetworkOverlay" :can-cancel="false">
            <div class="error-message">
                <b-icon icon="exclamation-triangle" type="is-warning" />
                <h1>Connection Error</h1>
                <p>Please check your internet connection. Attempting to reconnect...</p>
            </div>
        </b-loading>

        <TwoFactorChallenge v-if="isAuthenticated && twoFactorNeeded" />

        <template v-else>
          <div id="sidebar" v-if="isAuthenticated && !isOnPasswordReset">
            <sidebar v-if="authUser && !authUser.via_invite" />
            <guest-sidebar v-if="authUser && authUser.via_invite" />
          </div>

          <div id="main" :class="{ 'no-auth': isAuthenticated === false || isOnPasswordReset }">
            <div class="container is-fluid">
              <disclosure-statement v-if="authUser && (userFromShareLink || authUser.via_invite)" />
              <heading v-if="isAuthenticated && !isOnPasswordReset && loading === false" />
              <router-view />
              <div id="copyright" v-if="isAuthenticated && !isOnPasswordReset && loading === false">
                <p>{{ new Date().getFullYear() }} &copy; {{ title }}. All rights reserved.</p>
              </div>
            </div>
          </div>

          <template v-if="isAuthenticated && !isOnPasswordReset">
            <global-search />

            <b-modal class="session-activity-modal"
                     :active="isTimeoutModalShown" has-modal-card>
              <session-activity />
            </b-modal>
            <file-download-poller v-if="hasPendingExports" />
          </template>

          <div id="awn-toast-container" class="awn-bottom-left"></div>
          <tour ref="accountTour"></tour>
        </template>
    </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { INIT_STATUS } from '@/store/actions/init'
import { AUTH_CLEAR_TOKEN } from '@/store/actions/auth'
import Sidebar from '@/components/Nav/Sidebar.vue'
import GuestSidebar from '@/components/Nav/GuestSidebar.vue'
import GlobalSearch from '@/components/Nav/GlobalSearch.vue'
import SessionActivity from '@/components/Modals/SessionActivity.vue'
import Heading from '@/components/Page/Heading'
import FileDownloadPoller from '@/components/Account/FileDownloadPoller'
import Tour from '@/components/Account/Tour'

import { mapState, mapGetters, mapMutations } from 'vuex'
import DisclosureStatement from './components/Account/DisclosureStatement.vue'
import TwoFactorChallenge from './components/TwoFactorChallenge.vue'

export default {
  name: 'App',
  components: {
    TwoFactorChallenge,
    DisclosureStatement,
    Sidebar,
    GuestSidebar,
    GlobalSearch,
    Heading,
    SessionActivity,
    FileDownloadPoller,
    Tour
  },
  data () {
    return {
      title: process.env.NAME,
      activityPolling: null,
      networkCheckTimer: false
    }
  },
  computed: {
    ...mapState({
      init: state => state.init.all,
      apiOnline: 'apiOnline',
      loading: 'loading',
      navIsExpanded: 'navIsExpanded',
      searchIsExpanded: 'searchIsExpanded',
      underModalLink: 'underModalLink',
      authenticatedToken: 'authenticatedToken',
      lastActivity: 'lastActivity',
      isTimeoutModalShown: 'isTimeoutModalShown'
    }),
    ...mapGetters({
      pusher: 'pusher/init',
      isAuthenticated: 'isAuthenticated',
      twoFactorNeeded: 'twoFactorNeeded',
      authUser: 'authUser',
      userCan: 'userCan',
      userFromShareLink: 'isFromShareLink',
      userFromInviteLink: 'isFromInviteLink',
      hasPendingExports: 'hasPendingExports'
    }),
    appClass: function () {
      return [
        { 'nav-is-expanded': this.navIsExpanded && this.isAuthenticated !== false },
        { 'login': (this.isAuthenticated === false && !this.loading) || this.twoFactorNeeded === true },
        { 'from-shared-link': this.userFromShareLink === true },
        { 'from-invite-link': this.userFromInviteLink === true },
        { 'search-is-expanded': this.searchIsExpanded === true },
        { 'modal-is-open': this.underModalLink !== null }
      ]
    },
    isOnPasswordReset () {
      return this.$route.name === 'reset-password'
    },
    showNetworkOverlay () {
      return this.apiOnline === false
    }
  },
  watch: {
    init: function (newVal, oldVal) {
      if (oldVal === false && newVal !== false) this.boot()
    },
    isAuthenticated: function (newVal, oldVal) {
      if (newVal === true) this.boot()

      if (newVal === false && oldVal === true) {
        if (this.pusher) {
          this.pusher.allChannels()
            .forEach(channel => this.pusher.unsubscribe(channel.name))
        }

        clearInterval(this.activityPolling)
      }
    }
  },
  methods: {
    boot () {
      if (!this.isAuthenticated || this.init === false) return
      this.$store.dispatch('pusher/initPusher')
      this.activityPolling = setInterval(this.checkIfActive, 30000)

      // eslint-disable-next-line no-undef
      FS.identify(this.init.auth_user.id, {
        displayName: this.init.auth_user.full_name,
        email: this.init.auth_user.email,
        company: this.init.auth_user.company.name
      })
    },
    checkIfActive () {
      if (!this.isAuthenticated || this.userFromShareLink || this.init.auth_user.via_invite) return
      if (moment.isMoment(this.lastActivity) && !this.isTimeoutModalShown) {
        const minutesSinceActivity = moment().diff(this.lastActivity, 'minutes')
        if (minutesSinceActivity >= 58) {
          this.$store.dispatch('showTimeoutModal')
          if (this.pusher) {
            this.pusher.allChannels()
              .forEach(channel => this.pusher.unsubscribe(channel.name))
          }
        }
      }
    },
    handleResize () {
      if (_.isNull(localStorage.getItem('navIsExpanded'))) {
        this.forceSidebarState(window.innerWidth >= 768)
      }
    },
    ...mapMutations([
      'toggleSidebar',
      'forceSidebarState'
    ]),
    showAccountTour () {
      this.$refs.accountTour.showTour()
    },

    checkStatus () {
      this.$store.dispatch(INIT_STATUS).then(() => {
        clearTimeout(this.timer)
        if (this.apiOnline === false) {
          this.$store.commit('setApiStatus', true)
          this.$router.go() // refresh the page
        }
      })
    }
  },
  mounted () {
    this.boot()
    window.addEventListener('resize', this.handleResize)
    if (window.innerWidth < 768 && this.navIsExpanded) {
      this.forceSidebarState(false)
    }
  },
  beforeDestroy () {
    clearInterval(this.activityPolling)
    window.removeEventListener('resize', this.handleResize)
  },
  created: function () {
    const $store = this.$store
    const $router = this.$router
    const apiOnline = this.apiOnline

    this.$api.interceptors.response.use(function (response) {
      $store.commit('setApiStatus', true)
      return response
    }, (err) => {
      return new Promise((resolve, reject) => {
        if (err.response !== undefined) {
          if (err.response.status === 429) {
            this.$buefy.toast.open({
              duration: 4000,
              message: err.response.data.message,
              type: 'is-danger',
              queue: false
            })

            throw err
          }
        }

        if (err.response !== undefined) {
          if (err.response.status === 503) { // Down for maintenance
            $router.push({ name: 'maintenance' })
            return reject(err)
          }
        }

        if (err.response !== undefined) {
          if (err.response.status === 401 && this.isAuthenticated) {
            this.$buefy.snackbar.open({
              message: 'You have been logged out, session expired.',
              type: 'is-light',
              actionText: null,
              indefinite: true,
              queue: false
            })

            $store.dispatch(AUTH_CLEAR_TOKEN)

            const loginPath = window.location.pathname

            let queryString = { r: loginPath }
            if (loginPath === '/maintenance' || loginPath === '/' || loginPath === '/login') {
              queryString = undefined
            }

            $router.push({ name: 'login', query: queryString })

            return reject(err)
          }
        }

        /**
           * If we land on "Network Error" it could be from CORS
           * or it could mean the api server is down.
           *
           * When first landing here, we can check /status to see if the server
           * is down, if that request fails, we know it's down.
           *
           * Otherwise, we can assume the "Network Error" is CORS related.
           * - response was not JSON
           * - incorrect headers
           * - etc..
           *
           * src: https://github.com/axios/axios/issues/383
           * src: https://github.com/axios/axios/issues/1174
           */
        if (err.message === 'Network Error' || err.message.includes('timeout of')) {
          if ($router.currentRoute.name === 'maintenance') {
            return reject(err)
          }

          if (err.config.url.includes('/status')) {
            return reject(err)
          }

          console.error('API Response Error: ' + err.config.url)

          if (apiOnline === true) {
            $store.dispatch(INIT_STATUS).then(response => {
              if (!response || response.response !== 'success') {
                $store.commit('setApiStatus', false)

                this.checkStatus()
                this.timer = window.setInterval(() => {
                  this.checkStatus()
                }, 30 * 1000)

                return reject(err)
              }
            })
              .catch(() => {
                $store.commit('setApiStatus', false)

                this.checkStatus()
                this.timer = window.setInterval(() => {
                  this.checkStatus()
                }, 30 * 1000)
              })
          }
        }

        reject(err)
      })
    })
  }
}
</script>



// WEBPACK FOOTER //
// src/App.vue